import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  getTestimonialAdmin,
  updateTestimonial,
  deleteTestimonial,
} from "@/lib/api/marketing.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { MediaField } from "@/components/admin/MediaPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { imageFromMarketing, type MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/marketing/testimonials/$id")({
  loader: ({ params }) => getTestimonialAdmin({ data: { id: params.id } }),
  component: EditTestimonialPage,
});

function EditTestimonialPage() {
  const item = Route.useLoaderData();
  const navigate = useNavigate();
  if (!item) throw notFound();

  const [quote, setQuote] = useState(item.quote);
  const [name, setName] = useState(item.name);
  const [role, setRole] = useState(item.role);
  const [image, setImage] = useState<MediaSelection>(imageFromMarketing(item));
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateTestimonial({
        data: {
          id: item.id,
          quote,
          name,
          role,
          imageMediaId: image.mediaId,
          imageUrl: image.url,
          status,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete testimonial from ${item.name}?`)) return;
    await deleteTestimonial({ data: { id: item.id } });
    toast.success("Deleted");
    navigate({ to: "/admin/marketing/testimonials" });
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Edit testimonial"
        action={
          <Button variant="destructive" onClick={() => void handleDelete()}>
            Delete
          </Button>
        }
      />
      <MarketingAdminNav />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Quote</Label>
          <Textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={4} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} />
          </div>
        </div>
        <MediaField label="Portrait" value={image} onChange={setImage} />
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <AdminFormFooter cancelTo="/admin/marketing/testimonials" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
